import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { GraduationCap, Loader2, BookOpen, Users, Gift, Check, Mail, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    name: searchParams.get('name') || '',
    email: searchParams.get('email') || '',
    password: '',
    role: 'student',
    referralCode: searchParams.get('ref') || ''
  });
  const [loading, setLoading] = useState(false);
  const [referralValid, setReferralValid] = useState(null);
  const [referrerName, setReferrerName] = useState('');
  const [discountPercent, setDiscountPercent] = useState(0);
  const [verificationSent, setVerificationSent] = useState(false);
  const [resending, setResending] = useState(false);
  const redirectTo = searchParams.get('redirect');
  const fromAcademy = redirectTo?.includes('/academy');

  // Validate referral code when it changes
  useEffect(() => {
    const validateReferral = async () => {
      if (formData.referralCode.length >= 6) {
        try {
          const response = await axios.get(`${API}/referral/validate/${formData.referralCode}`);
          if (response.data.valid) {
            setReferralValid(true);
            setReferrerName(response.data.referrer_name);
            setDiscountPercent(response.data.discount_percent);
          } else {
            setReferralValid(false);
            setReferrerName('');
            setDiscountPercent(0);
          }
        } catch (error) {
          setReferralValid(false);
        }
      } else {
        setReferralValid(null);
      }
    };
    
    const debounce = setTimeout(validateReferral, 500);
    return () => clearTimeout(debounce);
  }, [formData.referralCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await register(
        formData.email, 
        formData.password, 
        formData.name, 
        formData.role,
        referralValid ? formData.referralCode : null
      );
      
      setVerificationSent(true);
      toast.success('Check your email to verify your account!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: formData.email, frontend_url: window.location.origin });
      toast.success('Verification email resent!');
    } catch (error) {
      toast.error('Failed to resend. Try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-lime-50 flex items-center justify-center p-4" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-bold text-2xl text-slate-900">AISkillFlow</span>
          </Link>
        </div>

        <Card className="border-slate-200 shadow-xl shadow-slate-200/50">
          <CardHeader className="text-center">
            <CardTitle className="font-heading text-2xl">
              {verificationSent ? 'Check your email' : 'Create your account'}
            </CardTitle>
            <CardDescription>
              {verificationSent 
                ? 'We sent a verification link to your email' 
                : fromAcademy 
                  ? 'Sign up to access AI Academy courses' 
                  : 'Start your learning journey today'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {verificationSent ? (
              <div className="text-center space-y-4" data-testid="verification-sent">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto">
                  <Mail className="w-8 h-8 text-indigo-600" />
                </div>
                <p className="text-sm text-slate-600">
                  We've sent a verification email to <strong>{formData.email}</strong>. 
                  Click the link in the email to verify your account, then log in.
                </p>
                <div className="flex flex-col gap-2 pt-2">
                  <Button
                    onClick={() => navigate('/login')}
                    className="w-full bg-primary hover:bg-primary/90 h-11"
                    data-testid="go-to-login-btn"
                  >
                    Go to Login
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleResendVerification}
                    disabled={resending}
                    className="w-full"
                    data-testid="resend-verification-btn"
                  >
                    {resending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Resend verification email
                  </Button>
                </div>
                <p className="text-xs text-slate-400">Didn't get it? Check your spam folder.</p>
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {fromAcademy && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-center">
                  <p className="text-indigo-800 font-medium text-sm">
                    <GraduationCap className="w-4 h-4 inline mr-1" />
                    You're signing up for AI Academy access
                  </p>
                  <p className="text-indigo-600 text-xs mt-0.5">18+ courses, audio lessons, AI Coach & more</p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="register-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="register-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Min. 6 characters"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="register-password"
                />
              </div>

              {/* Referral Code Input */}
              <div className="space-y-2">
                <Label htmlFor="referral" className="flex items-center gap-2">
                  <Gift className="w-4 h-4 text-secondary" />
                  Referral Code (optional)
                </Label>
                <div className="relative">
                  <Input
                    id="referral"
                    type="text"
                    placeholder="Enter referral code"
                    value={formData.referralCode}
                    onChange={(e) => setFormData({ ...formData, referralCode: e.target.value.toUpperCase() })}
                    className={`uppercase ${
                      referralValid === true ? 'border-green-500 pr-10' : 
                      referralValid === false ? 'border-red-500' : ''
                    }`}
                    data-testid="register-referral"
                  />
                  {referralValid === true && (
                    <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                  )}
                </div>
                {referralValid === true && (
                  <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-2 rounded-md">
                    <Gift className="w-4 h-4" />
                    <span>Referred by <strong>{referrerName}</strong> - Get <strong>{discountPercent}% off</strong> your first subscription!</span>
                  </div>
                )}
                {referralValid === false && formData.referralCode.length >= 6 && (
                  <p className="text-sm text-red-500">Invalid referral code</p>
                )}
              </div>

              <div className="space-y-3">
                <Label>I want to</Label>
                <RadioGroup
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                  className="grid grid-cols-2 gap-4"
                >
                  <div
                    onClick={() => setFormData({ ...formData, role: 'student' })}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.role === 'student' ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="role-student"
                  >
                    <RadioGroupItem value="student" id="student" className="sr-only" />
                    <BookOpen className={`w-6 h-6 ${formData.role === 'student' ? 'text-primary' : 'text-slate-400'}`} />
                    <span className={`font-medium ${formData.role === 'student' ? 'text-primary' : 'text-slate-600'}`}>
                      Learn
                    </span>
                    <span className="text-xs text-slate-500 text-center">Access all courses</span>
                  </div>
                  <div
                    onClick={() => setFormData({ ...formData, role: 'instructor' })}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.role === 'instructor' ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="role-instructor"
                  >
                    <RadioGroupItem value="instructor" id="instructor" className="sr-only" />
                    <Users className={`w-6 h-6 ${formData.role === 'instructor' ? 'text-primary' : 'text-slate-400'}`} />
                    <span className={`font-medium ${formData.role === 'instructor' ? 'text-primary' : 'text-slate-600'}`}>
                      Teach
                    </span>
                    <span className="text-xs text-slate-500 text-center">Create & sell courses</span>
                  </div>
                </RadioGroup>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary/90 h-11"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>
            )}

            {!verificationSent && (
            <div className="mt-6 text-center text-sm text-slate-600">
              Already have an account?{' '}
              <Link to="/login" className="text-primary font-medium hover:underline" data-testid="register-login-link">
                Sign in
              </Link>
            </div>
            )}

            {!verificationSent && (
            <div className="mt-4 text-center">
              <Link to="/academy" className="text-xs text-slate-400 hover:text-indigo-600 transition-colors">
                Browse AI Academy courses
              </Link>
            </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
